import { hubPageBg } from './accountHubStyles';
import { canManageHousehold, isInSameHousehold } from './accountHubUtils';
import {
  type FC,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ShieldIcon from '@mui/icons-material/Shield';
import TranslateIcon from '@mui/icons-material/Translate';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import PhoneIcon from '@mui/icons-material/Phone';
import SecurityIcon from '@mui/icons-material/Security';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import {
  setElevatedAuth,
  setElevatedAuthTotpEnabled,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import { setUserHouseholds } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import useGoogleTranslate from 'src/hooks/useGoogleTranslate';
import LanguageMenu from 'src/components/NewRenterInterface/NavBar/LanguageMenu';
import type { UHMember, UserHouseholdData } from 'src/types/Certification';
import VerifyCodeModal from './VerifyCodeModal';
import PendingVerificationsModal from 'src/components/Ulcm/PendingVerificationsModal';
import elevatedAuthService from 'src/services/elevatedAuth.service';
import { userService } from 'src/services/User.service';
import AddUserHouseholdMemberScreen from './AddUserHouseholdMemberScreen';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MenuItem {
  label: string;
  description: string;
  icon: ReactNode;
  path: string;
}

interface ManagedUser {
  userId: number;
  displayName: string;
  initials: string;
  householdName: string;
  propertyName: string;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const cardSx = {
  backgroundColor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
};

function deriveInitials(first: string, last: string): string {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}

// Keyboard activation helper for Box/Stack elements that carry an onClick
// handler. Pronto intentionally uses non-<button> wrappers for card-style
// click targets (to keep rich layout inside), so we have to bolt on the
// semantics ourselves: role="button", tabIndex=0, and Enter/Space activation.
function keyboardActivate(onActivate: () => void): (e: KeyboardEvent) => void {
  return (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate();
    }
  };
}

const focusVisibleSx = {
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
};

// ---------------------------------------------------------------------------
// AccountHubPage — single component for both the helper landing and per-user hub
//
// Route /renter/account-hub            → userId is undefined → shows helper landing
//                                        (profile + language + My Settings + Manage Other Users)
// Route /renter/account-hub/:userId    → shows per-user hub
//                                        (profile + language + settings menu)
// ---------------------------------------------------------------------------

const AccountHubPage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userId } = useParams<{ userId?: string }>();
  const { currentLanguageLabel } = useGoogleTranslate();
  const [langAnchor, setLangAnchor] = useState<HTMLElement | null>(null);
  const [showReverifyModal, setShowReverifyModal] = useState(false);
  const [showPendingVerifications, setShowPendingVerifications] =
    useState(false);
  const [showAddMember, setShowAddMember] = useState(false);

  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const userHouseholds = useSelector(
    (state: RootState) => state.newRenterInterface.userHouseholds
  ) as UserHouseholdData[];

  // ElevatedAuthGate already fetches totp_enabled from the server and stores
  // it in elevatedAuth — use that directly instead of a separate API call.
  const elevatedAuth = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.accountHub.elevatedAuth
  );
  const totpEnabled = elevatedAuth.totp_enabled;
  const elevatedAuthMethod = elevatedAuth.method;

  // Gate variants for "Manage Other Users":
  //  needsTotpSetup  — user hasn't set up TOTP at all
  //  needsTotpVerify — user has TOTP but elevated session was via OTP (email/SMS)
  const needsTotpSetup = !totpEnabled;
  const needsTotpVerify = totpEnabled && elevatedAuthMethod !== 'totp';
  const manageGated = needsTotpSetup || needsTotpVerify;

  // Whether this user has a qualifying elevated role (hoh / co_hoh / spouse)
  const isElevated = useMemo(
    () => canManageHousehold(userHouseholds),
    [userHouseholds]
  );

  // Derived flags
  const isManagementLanding = useMemo(() => userId === undefined, [userId]);
  const isSelf = userId === String(user?.id ?? '');
  const targetUserId = userId ? Number(userId) : null;

  // Refresh household data on every mount so the member list is never stale
  // after external mutations (e.g. adding a member via the household composition
  // modal). Silent background refetch — existing Redux data stays visible
  // until the response arrives.
  useEffect(() => {
    const refreshHouseholds = async (): Promise<void> => {
      try {
        const { data } = await userService.fetchMyHousehold();
        dispatch(setUserHouseholds(data ?? []));
      } catch {
        // Non-critical — existing Redux data remains visible.
      }
    };
    void refreshHouseholds();
  }, [dispatch]);

  useEffect(() => {
    if (!isManagementLanding) {
      return;
    }
    const refresh = async (): Promise<void> => {
      try {
        const res = await elevatedAuthService.getStatus();
        dispatch(setElevatedAuthTotpEnabled(res.totp_enabled));
      } catch {
        // Non-fatal: if the refresh fails, the existing Redux value is used.
      }
    };
    void refresh();
  }, [isManagementLanding, dispatch]);

  // Per-user hub: validate that the current user can access this userId.
  // If not, redirect to their own hub.
  const canAccess =
    isManagementLanding ||
    isSelf ||
    (isElevated &&
      targetUserId !== null &&
      isInSameHousehold(userHouseholds, targetUserId));

  // Helper landing: derive manageable users from userHouseholds
  const managedUsers = useMemo<ManagedUser[]>(() => {
    if (!isManagementLanding || !user) {
      return [];
    }
    const seen = new Set<number>();
    const result: ManagedUser[] = [];
    for (const uh of userHouseholds) {
      for (const member of uh.members) {
        const memberId = member.user_id;
        if (memberId == null || memberId === user.id || seen.has(memberId)) {
          continue;
        }
        seen.add(memberId);
        result.push({
          userId: memberId,
          displayName: `${member.first_name} ${member.last_name}`.trim(),
          initials: deriveInitials(member.first_name, member.last_name),
          householdName: uh.name,
          propertyName: '', // UserHousehold has no property directly; leave blank
        });
      }
    }
    return result;
  }, [userHouseholds, isManagementLanding, user]);

  // When viewing another user's hub, look them up from userHouseholds.
  // This gives us their name — email/phone are only available for self.
  const targetMember = useMemo<UHMember | null>(() => {
    if (isSelf || isManagementLanding || targetUserId === null) {
      return null;
    }
    for (const uh of userHouseholds) {
      const found = uh.members.find((m) => m.user_id === targetUserId);
      if (found) {
        return found;
      }
    }
    return null;
  }, [userHouseholds, isManagementLanding, isSelf, targetUserId]);

  // Primary household for adding members: prefer hoh/co_hoh role, fall back to first.
  const primaryHouseholdId = useMemo(
    () =>
      (
        userHouseholds.find(
          (uh) => uh.my_role === 'hoh' || uh.my_role === 'co_hoh'
        ) ?? userHouseholds[0]
      )?.user_household_id ?? null,
    [userHouseholds]
  );

  const handleMemberAdded = useCallback(async (): Promise<void> => {
    setShowAddMember(false);
    try {
      const { data } = await userService.fetchMyHousehold();
      dispatch(setUserHouseholds(data ?? []));
    } catch {
      // Non-critical: UI will refresh on next login
    }
  }, [dispatch]);

  if (!isManagementLanding && !canAccess && user) {
    return (
      <Navigate
        to={`/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${user.id}`}
        replace
      />
    );
  }

  const hubDefaultRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}`;
  const baseRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${
    userId ?? ''
  }`;

  // Back route: per-user hub shows a back arrow to the helper landing only
  // when the current user is elevated.
  const backRoute =
    !isManagementLanding && isElevated ? hubDefaultRoute : undefined;

  // Profile data: use target member data when viewing another user
  const profileInitials =
    isSelf || isManagementLanding
      ? `${user?.first_name?.[0] ?? ''}${
          user?.last_name?.[0] ?? ''
        }`.toUpperCase() || '?'
      : deriveInitials(
          targetMember?.first_name ?? '',
          targetMember?.last_name ?? ''
        );
  const profileDisplayName =
    isSelf || isManagementLanding
      ? user?.displayName ?? '—'
      : `${targetMember?.first_name ?? ''} ${
          targetMember?.last_name ?? ''
        }`.trim() || '—';
  const profileEmail =
    isSelf || isManagementLanding
      ? user?.email && !user.email.endsWith('@prontohousing.io')
        ? user.email
        : null
      : targetMember?.emails?.find((e) => e.is_primary)?.email ??
        targetMember?.emails?.[0]?.email ??
        null;
  const profilePhone =
    isSelf || isManagementLanding
      ? user?.primary_mobile_phone ?? null
      : targetMember?.phones?.find((p) => p.is_primary)?.phone_number ??
        targetMember?.phones?.[0]?.phone_number ??
        null;

  // Per-user settings menu items
  const menuItems: MenuItem[] = [
    {
      label: 'Login & Security',
      description: 'Password, 2FA, and sessions',
      icon: <LockIcon sx={{ fontSize: 20, color: 'text.secondary' }} />,
      path: `${baseRoute}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}`,
    },
    {
      label: 'Contact Information',
      description: 'Email, phone, and address',
      icon: <EmailIcon sx={{ fontSize: 20, color: 'text.secondary' }} />,
      path: `${baseRoute}/${RENTER_PATHS.ACCOUNT_HUB_CONTACT_INFO}`,
    },
    {
      label: 'Personal Information',
      description: 'DOB, SSN, demographics',
      icon: <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />,
      path: `${baseRoute}/${RENTER_PATHS.ACCOUNT_HUB_PERSONAL_INFO}`,
    },
    {
      label: 'Data & Privacy',
      description: 'Download or delete your data',
      icon: <ShieldIcon sx={{ fontSize: 20, color: 'text.secondary' }} />,
      path: `${baseRoute}/${RENTER_PATHS.ACCOUNT_HUB_DATA_PRIVACY}`,
    },
    {
      label: 'Property List',
      description: 'Properties associated with this account',
      icon: <HomeWorkIcon sx={{ fontSize: 20, color: 'text.secondary' }} />,
      path: `${baseRoute}/${RENTER_PATHS.ACCOUNT_HUB_PROPERTY_LIST}`,
    },
  ];

  return (
    <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
      <PageHeader
        title="Account Hub"
        icon={<PersonIcon />}
        subtitle="Manage your account and settings"
        backRoute={backRoute}
      />

      <Stack gap={2.5} sx={{ p: 3 }}>
        {/* ── Profile card (shared) ── */}
        <Box sx={{ ...cardSx, p: 2 }}>
          <Stack gap={2.5}>
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '28px',
                  backgroundColor: 'primary.lighter',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    color: 'primary.main',
                    fontSize: 20,
                    fontWeight: 600,
                    lineHeight: 1,
                  }}
                >
                  {profileInitials}
                </Typography>
              </Box>
              <Typography
                sx={{ color: 'text.primary', fontSize: 18, fontWeight: 600 }}
              >
                {profileDisplayName}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" gap={1}>
              <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Stack gap={0.25}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Primary Email
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: profileEmail ? 'text.primary' : 'text.disabled',
                    wordBreak: 'break-all',
                  }}
                >
                  {profileEmail ?? 'No email'}
                </Typography>
              </Stack>
            </Stack>

            {profilePhone && (
              <Stack direction="row" alignItems="center" gap={1}>
                <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Stack gap={0.25}>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    Primary Phone
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'text.primary',
                    }}
                  >
                    {profilePhone}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Box>

        {/* ── Language card (shared — only for self / helper landing) ── */}
        {(isManagementLanding || isSelf) && (
          <Box
            role="button"
            tabIndex={0}
            aria-label={`Change language, current: ${currentLanguageLabel}`}
            sx={{ ...cardSx, p: 2, cursor: 'pointer', ...focusVisibleSx }}
            onClick={(e) => setLangAnchor(e.currentTarget)}
            onKeyDown={keyboardActivate(() => {
              // We need a real DOM node for the menu anchor; read it from the
              // event target. This keeps the UX identical to mouse clicks.
              setLangAnchor(
                (document.activeElement as HTMLElement | null) ?? null
              );
            })}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" alignItems="center" gap={1.5}>
                <TranslateIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                  Language
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                  {currentLanguageLabel}
                </Typography>
                <ChevronRightIcon
                  sx={{ fontSize: 20, color: 'text.secondary' }}
                />
              </Stack>
            </Stack>
          </Box>
        )}

        <LanguageMenu
          anchorEl={langAnchor}
          onClose={() => setLangAnchor(null)}
        />

        {/* ── HELPER LANDING: My Settings + Manage Other Users ── */}
        {isManagementLanding && (
          <>
            <Box sx={{ ...cardSx, p: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                role="button"
                tabIndex={0}
                aria-label="Open My Settings"
                onClick={() => navigate(`${hubDefaultRoute}/${user?.id ?? ''}`)}
                onKeyDown={keyboardActivate(() =>
                  navigate(`${hubDefaultRoute}/${user?.id ?? ''}`)
                )}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: 'action.hover' },
                  ...focusVisibleSx,
                }}
              >
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <SettingsIcon
                    sx={{ fontSize: 20, color: 'text.secondary' }}
                  />
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                    My Settings
                  </Typography>
                </Stack>
                <ChevronRightIcon
                  sx={{ fontSize: 20, color: 'text.secondary' }}
                />
              </Stack>
            </Box>

            <Typography
              sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}
            >
              Manage Other Users
            </Typography>

            {/* 2FA gate: blur the cards, message pinned to top of overlay */}
            <Box sx={{ position: 'relative' }}>
              {/* User cards — blurred + tinted when TOTP not set up */}
              <Stack
                gap={2}
                sx={
                  manageGated && managedUsers.length > 0
                    ? {
                        filter: 'blur(3px)',
                        pointerEvents: 'none',
                        userSelect: 'none',
                        opacity: 0.55,
                      }
                    : undefined
                }
              >
                {managedUsers.length === 0 && !manageGated ? (
                  <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                      No other users to manage.
                    </Typography>
                  </Box>
                ) : managedUsers.length > 0 ? (
                  managedUsers.map((managed, i) => {
                    const isEven = i % 2 === 0;
                    return (
                      <Box
                        key={managed.userId}
                        role="button"
                        tabIndex={manageGated ? -1 : 0}
                        aria-label={`Manage account for ${managed.displayName}`}
                        sx={{
                          ...cardSx,
                          p: 2,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'grey.50' },
                          ...focusVisibleSx,
                        }}
                        onClick={() =>
                          navigate(`${hubDefaultRoute}/${managed.userId}`)
                        }
                        onKeyDown={keyboardActivate(() =>
                          navigate(`${hubDefaultRoute}/${managed.userId}`)
                        )}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Stack direction="row" alignItems="center" gap={1.5}>
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '24px',
                                backgroundColor: isEven
                                  ? 'secondary.lighter'
                                  : 'info.lighter',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: isEven
                                    ? 'secondary.dark'
                                    : 'info.dark',
                                  fontSize: 18,
                                  fontWeight: 600,
                                  lineHeight: 1,
                                }}
                              >
                                {managed.initials}
                              </Typography>
                            </Box>
                            <Stack gap={0.5}>
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: 'text.primary',
                                }}
                              >
                                {managed.displayName}
                              </Typography>
                              <Typography
                                sx={{ fontSize: 12, color: 'text.secondary' }}
                              >
                                {managed.propertyName || managed.householdName}
                              </Typography>
                            </Stack>
                          </Stack>
                          <ChevronRightIcon
                            sx={{
                              fontSize: 24,
                              color: 'text.secondary',
                              flexShrink: 0,
                            }}
                          />
                        </Stack>
                      </Box>
                    );
                  })
                ) : null}
              </Stack>

              {/* 2FA gate overlay — gray tint + card at the top */}
              {manageGated && managedUsers.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.18)',
                    borderRadius: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    pt: 2,
                    px: 2,
                    pointerEvents: 'none',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: 'background.paper',
                      borderRadius: '12px',
                      p: 2.5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1.5,
                      textAlign: 'center',
                      boxShadow: 3,
                      width: '100%',
                      maxWidth: 360,
                      pointerEvents: 'auto',
                    }}
                  >
                    <SecurityIcon
                      sx={{ fontSize: 32, color: 'primary.main' }}
                    />
                    {needsTotpSetup ? (
                      <>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'primary.dark',
                          }}
                        >
                          Two-Factor Authentication required
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: 'text.secondary',
                            maxWidth: 280,
                          }}
                        >
                          To manage other users, you must first set up an
                          authenticator app on your account.
                        </Typography>
                        <Button
                          variant="contained"
                          disableElevation
                          size="small"
                          startIcon={<SecurityIcon />}
                          onClick={() =>
                            navigate(
                              `${hubDefaultRoute}/${user?.id ?? ''}/${
                                RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY
                              }/${
                                RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA
                              }/${
                                RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA_SETUP
                              }`
                            )
                          }
                          sx={{ mt: 0.5, borderRadius: '8px', fontWeight: 600 }}
                        >
                          <span>Set up 2FA</span>
                        </Button>
                      </>
                    ) : (
                      <>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'primary.dark',
                          }}
                        >
                          Authenticator verification required
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: 'text.secondary',
                            maxWidth: 280,
                          }}
                        >
                          To manage other users, please re-verify your session
                          using your authenticator app.
                        </Typography>
                        <Button
                          variant="contained"
                          disableElevation
                          size="small"
                          startIcon={<SecurityIcon />}
                          onClick={() => setShowReverifyModal(true)}
                          sx={{ mt: 0.5, borderRadius: '8px', fontWeight: 600 }}
                        >
                          <span>Re-verify with authenticator</span>
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* ── ENG-2730: Inline 2FA card for HoH with no managed users yet ──────
            The existing 2FA overlay (above) only renders when managedUsers.length > 0
            because it's positioned absolute over blurred user cards. When a HoH has
            zero managed users they see "No other users to manage." with no explanation
            of why they can't add new members. This inline card fills that gap. ── */}
        {isManagementLanding &&
          manageGated &&
          managedUsers.length === 0 &&
          canManageHousehold(userHouseholds) && (
            <Box
              sx={{
                ...cardSx,
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                textAlign: 'center',
              }}
            >
              <SecurityIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              {needsTotpSetup ? (
                <>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'primary.dark',
                    }}
                  >
                    Two-Factor Authentication required
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: 'text.secondary',
                      maxWidth: 280,
                    }}
                  >
                    To add or manage household members, you must first set up an
                    authenticator app on your account.
                  </Typography>
                  <Button
                    variant="contained"
                    disableElevation
                    size="small"
                    startIcon={<SecurityIcon />}
                    onClick={() =>
                      navigate(
                        `${hubDefaultRoute}/${user?.id ?? ''}/${
                          RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY
                        }/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA}/${
                          RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA_SETUP
                        }`
                      )
                    }
                    sx={{ mt: 0.5, borderRadius: '8px', fontWeight: 600 }}
                  >
                    <span>Set up 2FA</span>
                  </Button>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'primary.dark',
                    }}
                  >
                    Authenticator verification required
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: 'text.secondary',
                      maxWidth: 280,
                    }}
                  >
                    To add or manage household members, please re-verify your
                    session using your authenticator app.
                  </Typography>
                  <Button
                    variant="contained"
                    disableElevation
                    size="small"
                    startIcon={<SecurityIcon />}
                    onClick={() => setShowReverifyModal(true)}
                    sx={{ mt: 0.5, borderRadius: '8px', fontWeight: 600 }}
                  >
                    <span>Re-verify with authenticator</span>
                  </Button>
                </>
              )}
            </Box>
          )}

        {/* ── Add New Member ──────────────────────────────────────────────────
            ENG-2730: hidden when manageGated — the inline 2FA card above tells
            the user what to do first. Once 2FA is set up this button appears. ── */}
        {isManagementLanding &&
          canManageHousehold(userHouseholds) &&
          !manageGated &&
          primaryHouseholdId !== null &&
          (showAddMember ? (
            <AddUserHouseholdMemberScreen
              userHouseholdId={primaryHouseholdId}
              onSubmit={() => void handleMemberAdded()}
              onCancel={() => setShowAddMember(false)}
            />
          ) : (
            <Box
              sx={{
                ...cardSx,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => setShowAddMember(true)}
                sx={{ borderRadius: '8px', fontWeight: 600 }}
              >
                <span>Add New Member</span>
              </Button>
            </Box>
          ))}

        {/* ── PER-USER HUB: settings menu ── */}
        {!isManagementLanding && (
          <>
            <Box sx={{ ...cardSx, overflow: 'hidden', p: 2 }}>
              {menuItems.map((item, i) => (
                <Box key={item.label}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    role="button"
                    tabIndex={0}
                    aria-label={`${item.label} — ${item.description}`}
                    onClick={() => navigate(item.path)}
                    onKeyDown={keyboardActivate(() => navigate(item.path))}
                    sx={{
                      py: 1.5,
                      cursor: 'pointer',
                      borderRadius: '8px',
                      '&:hover': { backgroundColor: 'action.hover' },
                      ...focusVisibleSx,
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      {item.icon}
                      <Stack gap={0.25}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'text.primary',
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 12, color: 'text.secondary' }}
                        >
                          {item.description}
                        </Typography>
                      </Stack>
                    </Stack>
                    <ChevronRightIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.secondary',
                        flexShrink: 0,
                      }}
                    />
                  </Stack>
                  {i < menuItems.length - 1 && (
                    <Divider sx={{ borderColor: 'grey.300' }} />
                  )}
                </Box>
              ))}
            </Box>

            <Stack direction="row" gap={2} justifyContent="center">
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Terms of Service
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                •
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Privacy Policy
              </Typography>
            </Stack>
          </>
        )}
      </Stack>

      {showPendingVerifications && (
        <PendingVerificationsModal
          onDismiss={() => setShowPendingVerifications(false)}
          onAllVerified={() => setShowPendingVerifications(false)}
        />
      )}

      {showReverifyModal && !showPendingVerifications && (
        <VerifyCodeModal
          purpose="elevated_auth"
          totpEnabled={true}
          hideFallback={true}
          onSuccess={(expiresAt, method) => {
            dispatch(
              setElevatedAuth({
                verified: true,
                expires_at: expiresAt,
                verified_at: new Date().toISOString(),
                method,
              })
            );
            setShowReverifyModal(false);
          }}
          onCancel={() => setShowReverifyModal(false)}
          onUnverifiedContact={() => setShowPendingVerifications(true)}
          dismissable
        />
      )}
    </Box>
  );
};

export default AccountHubPage;
